import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, UserCog, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TailAdminDataTable,
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const RolePicker = ({ roles, selected, onChange }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {roles.map((role) => (
      <label key={role.role_key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3">
        <Checkbox
          checked={selected.includes(role.role_key)}
          onCheckedChange={(checked) => onChange(checked ? [...selected, role.role_key] : selected.filter((item) => item !== role.role_key))}
          aria-label={role.name}
        />
        <span><span className="block text-sm font-medium text-gray-900">{role.name}</span><span className="block text-xs text-gray-500">{role.description}</span></span>
      </label>
    ))}
  </div>
);

const AdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [editReason, setEditReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [staffResponse, rolesResponse] = await Promise.all([adminAPI.getStaff(search || null), adminAPI.getStaffRoles()]);
      setStaff(staffResponse.data?.staff || []);
      setRoles(rolesResponse.data?.roles || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load staff");
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const chooseStaff = (item) => { setSelected(item); setEditRoles(item.roles || []); setEditReason(""); };

  const saveRoles = async () => {
    if (!selected || editReason.trim().length < 5) return toast.error("Add a reason for this role change");
    try {
      await adminAPI.updateStaffRoles(selected.adminId, editRoles, editReason);
      toast.success("Roles updated"); await load();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to update roles"); }
  };

  const toggleStatus = async () => {
    const reason = window.prompt(`Reason to ${selected.status === "active" ? "suspend" : "activate"} this staff account?`);
    if (!reason || reason.trim().length < 5) return;
    try {
      await adminAPI.updateStaffStatus(selected.adminId, selected.status === "active" ? "suspended" : "active", reason);
      toast.success("Staff status updated"); setSelected(null); await load();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to update status"); }
  };

  const rows = useMemo(() => staff.map((item) => ({
    id: item.adminId,
    cells: [
      <div><p className="font-semibold text-gray-900">{item.name}</p><p className="text-xs text-gray-500">{item.email}</p></div>,
      <div className="flex flex-wrap gap-1">{(item.roles || []).map((role) => <TailAdminStatusBadge key={role}>{role.replaceAll("_", " ")}</TailAdminStatusBadge>)}</div>,
      <TailAdminStatusBadge tone={item.status === "active" ? "success" : "danger"}>{item.status}</TailAdminStatusBadge>,
      <Button variant="outline" size="sm" onClick={() => chooseStaff(item)}><UserCog className="h-4 w-4" /> Manage</Button>,
    ],
  })), [staff]);

  return <div className="space-y-6">
    <TailAdminPageHeader eyebrow="Access governance" title="Staff & roles" description="Review staff and assign least-privilege role bundles. New accounts are created only through private invitations." actions={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /> Refresh</Button>} />
    <div className="relative max-w-xl"><Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" /><Input className="pl-9" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search staff, email or role" aria-label="Search staff" /></div>
    <div className="grid gap-6 xl:grid-cols-[1.4fr_0.6fr]"><TailAdminPanel title="Staff directory" description={`${staff.length} staff accounts`} >{loading || rows.length ? <TailAdminDataTable loading={loading} columns={["Staff", "Roles", "Status", "Actions"]} rows={rows} /> : <TailAdminEmptyState icon={UsersRound} title="No staff found" description="No staff match this search." />}</TailAdminPanel><TailAdminPanel title="Role assignment" description="Select a staff member to review access.">{selected ? <div className="space-y-4"><div><p className="font-semibold text-gray-900">{selected.name}</p><p className="text-sm text-gray-500">{selected.email}</p></div><RolePicker roles={roles} selected={editRoles} onChange={setEditRoles} /><div><Label>Reason</Label><Input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Why is access changing?" /></div><div className="flex flex-wrap gap-2"><Button onClick={saveRoles}>Save roles</Button><Button variant="outline" onClick={toggleStatus}>{selected.status === "active" ? "Suspend" : "Activate"}</Button></div><p className="text-xs text-gray-500">You cannot change your own roles or status. The final active superadmin is protected.</p></div> : <TailAdminEmptyState icon={UserCog} title="Select staff" description="Role and status controls will appear here." />}</TailAdminPanel></div>
  </div>;
};

export default AdminStaff;
